import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, FileDown, RefreshCw, BadgeCheck, Wrench, FolderCheck, Download } from "lucide-react";
import { careerCockpitApi } from "@/api/careerCockpit";
import { downloadVaultAsset } from "@/api/compliance";
import CreditPaywall from "@/components/billing/CreditPaywall";

const AVAIL_EXAMPLE =
  "I am trained and willing to be trained in any tickets that have expired to fit your needs.";

const card = "border-4 border-charcoal rounded-2xl bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]";
const label = "font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/70 mb-2";
const ta = "w-full bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal resize-none";

export default function ResumeRefresh() {
  const [current, setCurrent] = useState("");
  const [added, setAdded] = useState("");
  const [avail, setAvail] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [saved, setSaved] = useState([]);

  const loadSaved = () =>
    careerCockpitApi.savedResumes().then((d) => setSaved(d.items || [])).catch(() => {});

  // Prefill the current resume from any saved base resume + load saved resumes
  // (server-side, so they show on any device — computer or mobile).
  useEffect(() => {
    careerCockpitApi.context()
      .then((d) => { if (d?.base_resume) setCurrent((c) => c || d.base_resume); })
      .catch(() => {});
    loadSaved();
  }, []);

  const generate = async () => {
    if (current.trim().length < 40) { toast.error("Paste your current resume first"); return; }
    setBusy(true); setRes(null);
    try {
      const d = await careerCockpitApi.refreshResume({
        current_resume: current.trim().slice(0, 8000),
        added_experience: added.trim(),
        availability_note: avail.trim(),
      });
      setRes(d);
      loadSaved();  // it's already saved to the Career Vault server-side — refresh the list
      toast.success(d.charged ? `Resume refreshed · ${d.cost} Credits spent` : "Resume refreshed & saved to your Career Vault");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && detail) setPaywall(detail);
      else toast.error(typeof detail === "string" ? detail : "Refresh failed");
    } finally { setBusy(false); }
  };

  const download = async (assetId, filename) => {
    if (!assetId) return;
    try { await downloadVaultAsset(assetId, filename || "resume.pdf"); }
    catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-3" data-testid="resume-refresh">
      <div className="border-4 border-charcoal rounded-2xl bg-charcoal p-3.5 shadow-[3px_3px_0px_#1A1A1A]">
        <div className="flex items-center gap-2 mb-1">
          <RefreshCw size={15} className="text-gold" />
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold">Resume Refresh</p>
        </div>
        <p className="font-mono text-[9px] text-white/60 leading-snug">
          Paste your existing resume, add what you've done since, then generate a modern 2026-style PDF resume + a general cover note.
        </p>
      </div>

      {/* 1 · Current resume */}
      <div className={card}>
        <div className="flex items-center justify-between">
          <p className={label}>1 · Current Resume</p>
          <span className={`font-mono text-[8px] ${current.length > 7500 ? "text-rust" : "text-charcoal/40"}`}>{current.length}/8000</span>
        </div>
        <textarea value={current} rows={7} maxLength={8000} onChange={(e) => setCurrent(e.target.value)} data-testid="refresh-current-input" placeholder="Paste your full current resume here…" className={ta} />
      </div>

      {/* 2 · Added experience */}
      <div className={card}>
        <p className={label}>2 · Added Jobs, Tickets & Licences (since)</p>
        <textarea value={added} rows={4} onChange={(e) => setAdded(e.target.value)} data-testid="refresh-added-input" placeholder="e.g. 2024–2026 Site Supervisor at ACME · White Card renewed · Forklift (LF) licence · First Aid (HLTAID011)…" className={ta} />
      </div>

      {/* 3 · Availability / reference note */}
      <div className={card}>
        <p className={label}>3 · Availability / Reference Note</p>
        <textarea value={avail} rows={3} onChange={(e) => setAvail(e.target.value)} data-testid="refresh-availability-input" placeholder={`Optional. Example:\n"${AVAIL_EXAMPLE}"`} className={ta} />
        <button onClick={() => setAvail(AVAIL_EXAMPLE)} data-testid="refresh-use-example" className="mt-2 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal/80 underline underline-offset-2">Use example wording</button>
      </div>

      <button onClick={generate} disabled={busy} data-testid="refresh-generate-btn" className="w-full flex items-center justify-center gap-2 bg-charcoal text-gold font-black font-mono text-[11px] uppercase tracking-widest py-3.5 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
        {busy ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><Sparkles size={15} /> Generate Resume + Cover Note · 30 Cr</>}
      </button>

      {/* Result */}
      {res && (
        <div className={`${card} space-y-3`} data-testid="refresh-result">
          <div className="flex items-center gap-1.5 bg-emerald-50 border-2 border-emerald-600 rounded-xl px-2.5 py-2" data-testid="refresh-vault-confirm">
            <FolderCheck size={14} className="text-emerald-700 shrink-0" />
            <p className="font-mono text-[9px] text-emerald-800 leading-snug">Saved to your <b>Career Vault</b> — open it on any device (computer or mobile).</p>
          </div>
          <button onClick={() => download(res.asset_id, res.filename)} data-testid="refresh-download" className="w-full flex items-center justify-center gap-2 bg-gold text-charcoal font-black font-mono text-[10px] uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[2px_2px_0px_#1A1A1A] active:translate-y-[1px] active:shadow-none">
            <FileDown size={14} /> Save to Device (PDF)
          </button>

          {res.headline && <p className="font-serif text-lg text-charcoal leading-tight">{res.headline}</p>}
          {res.summary && (
            <div><p className={label}>Professional Summary</p><p className="font-mono text-[11px] text-charcoal leading-relaxed">{res.summary}</p></div>
          )}
          {!!(res.experience || []).length && (
            <div><p className={label}>Experience</p>
              <ul className="space-y-1.5">{res.experience.map((b, i) => (
                <li key={i} className="flex gap-2 font-mono text-[11px] text-charcoal leading-snug"><span className="text-gold font-black">▸</span>{b}</li>
              ))}</ul>
            </div>
          )}
          {!!(res.skills || []).length && (
            <div><p className={label}>Skills</p>
              <div className="flex flex-wrap gap-1.5">{res.skills.map((s, i) => (
                <span key={i} className="font-mono text-[9px] bg-cream border-2 border-charcoal rounded-full px-2 py-0.5">{s}</span>
              ))}</div>
            </div>
          )}
          {!!(res.tickets_licences || []).length && (
            <div><p className={label}>Tickets & Licences</p>
              <ul className="space-y-1">{res.tickets_licences.map((t, i) => (
                <li key={i} className="flex gap-1.5 font-mono text-[10px] text-charcoal"><BadgeCheck size={12} className="text-emerald-600 shrink-0 mt-0.5" />{t}</li>
              ))}</ul>
            </div>
          )}
          {res.availability && (
            <div className="bg-cream border-2 border-charcoal rounded-xl p-2.5">
              <p className="flex items-center gap-1 font-mono text-[8px] uppercase tracking-widest text-charcoal/60 mb-1"><Wrench size={10} /> Availability</p>
              <p className="font-mono text-[10px] text-charcoal leading-snug">{res.availability}</p>
            </div>
          )}
          {res.cover_note && (
            <div><p className={label}>General Cover Note</p><p className="font-mono text-[10px] text-charcoal leading-relaxed whitespace-pre-line">{res.cover_note}</p></div>
          )}
        </div>
      )}
      {/* Saved resumes — server-side, visible on every device */}
      {saved.length > 0 && (
        <div className={card} data-testid="refresh-saved-list">
          <p className={label}>My Saved Resumes · Career Vault</p>
          <div className="space-y-1.5">
            {saved.map((it) => (
              <div key={it.asset_id} className="flex items-center gap-2 border-2 border-charcoal rounded-xl bg-cream px-2.5 py-2" data-testid={`refresh-saved-${it.asset_id}`}>
                <BadgeCheck size={13} className="text-emerald-600 shrink-0" />
                <span className="flex-1 truncate font-mono text-[10px] text-charcoal">{it.filename}</span>
                <button onClick={() => download(it.asset_id, it.filename)} data-testid={`refresh-saved-download-${it.asset_id}`} className="shrink-0 flex items-center gap-1 bg-charcoal text-gold font-black font-mono text-[8px] uppercase tracking-widest px-2.5 py-1.5 rounded-full border-2 border-charcoal active:translate-y-[1px]">
                  <Download size={11} /> Save
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <CreditPaywall detail={paywall} onClose={() => setPaywall(null)} />
    </div>
  );
}
